import { useMemo } from 'react'
import { Checkbox } from '@klear/ui/components/checkbox'
import { AgGridReact } from 'ag-grid-react'
import type {
  ColDef,
  ColTypeDef,
  DataTypeDefinition,
  ICellRendererParams,
  RowClassParams,
  RowStyle,
  Theme,
  ThemeDefaultParams,
  GridReadyEvent,
  ColDef as AgGridColDef
} from 'ag-grid-community'

interface TableAgGridBulkSelectionProps<T = unknown> {
  isBulkUpdating: boolean
  columnDefs: ColDef<T>[]
  className?: string
  rowData: T[]
  bulkItemSelected: (item: T) => boolean
  onBulkItemSelected: (item: T, isChecked: boolean) => void
  disableBulkSelectionItem?: (item: T) => boolean
  rowHeight: number
  headerHeight: number
  animateRows: boolean
  enableCellTextSelection: boolean
  pagination: boolean
  paginationPageSize: number
  theme: Theme<ThemeDefaultParams>
  onGridReady: (params: GridReadyEvent) => void
  quickFilterText: string
  defaultColDef: AgGridColDef
  columnTypes?: Record<string, ColTypeDef>
  dataTypeDefinitions?: { [cellDataType: string]: DataTypeDefinition }
  getRowStyle?: (params: RowClassParams<T>) => RowStyle | undefined
  getRowClass?: (params: RowClassParams<T>) => string | string[] | undefined
}

export default function TableAgGridBulkSelection<T = unknown>({
  isBulkUpdating,
  bulkItemSelected,
  onBulkItemSelected,
  disableBulkSelectionItem,
  columnDefs,
  className,
  rowHeight,
  headerHeight,
  animateRows,
  rowData,
  enableCellTextSelection,
  pagination,
  paginationPageSize,
  theme,
  onGridReady,
  quickFilterText,
  defaultColDef,
  columnTypes,
  dataTypeDefinitions,
  getRowStyle,
  getRowClass
}: TableAgGridBulkSelectionProps<T>) {
  const columnData = useMemo((): ColDef<T>[] => {
    if (!isBulkUpdating) return columnDefs
    return [
      {
        colId: 'bulkSelect',
        width: 36,
        minWidth: 36,
        maxWidth: 36,
        resizable: false,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        headerComponent: () => {
          const selectableItems = rowData.filter((item) => !disableBulkSelectionItem || !disableBulkSelectionItem(item))

          const selectedItems = selectableItems.filter((item) => bulkItemSelected(item))
          const allSelected = selectableItems.length > 0 && selectedItems.length === selectableItems.length
          const indeterminate = selectedItems.length > 0 && selectedItems.length < selectableItems.length

          const handleSelectAll = (checked: boolean) => {
            rowData.forEach((item) => {
              if (!disableBulkSelectionItem || !disableBulkSelectionItem(item)) {
                onBulkItemSelected(item, checked)
              }
            })
          }

          return (
            <div className="flex items-center justify-center h-full">
              <Checkbox
                checked={indeterminate ? 'indeterminate' : allSelected}
                onCheckedChange={handleSelectAll}
                disabled={selectableItems.length === 0}
              />
            </div>
          )
        },
        cellRenderer: (params: ICellRendererParams<T>) => {
          if (!params.data) return null
          return (
            <div className="flex items-center justify-center h-full p-2">
              <Checkbox
                checked={bulkItemSelected(params.data)}
                onCheckedChange={(val) => onBulkItemSelected(params.data!, val as boolean)}
                disabled={disableBulkSelectionItem ? disableBulkSelectionItem(params.data) : false}
              />
            </div>
          )
        }
      },
      ...columnDefs
    ]
  }, [isBulkUpdating, columnDefs, rowData, bulkItemSelected, onBulkItemSelected, disableBulkSelectionItem])

  return (
    <div className={className}>
      <AgGridReact
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        rowData={rowData}
        columnDefs={columnData}
        defaultColDef={defaultColDef}
        columnTypes={columnTypes}
        dataTypeDefinitions={dataTypeDefinitions}
        animateRows={animateRows}
        enableCellTextSelection={enableCellTextSelection}
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        theme={theme}
        onGridReady={onGridReady}
        quickFilterText={quickFilterText}
        getRowStyle={getRowStyle}
        getRowClass={getRowClass}
      />
    </div>
  )
}
